import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChatCircleDots, PaperPlaneRight, Plus, SpinnerGap, MagnifyingGlass, PushPin, Paperclip, Copy, Check, X, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <button onClick={handleCopy} className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title="Copy" data-testid="chat-copy-btn">
      {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
    </button>
  );
}

export default function ChatPage() {
  const { api } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState("");
  const [activeThread, setActiveThread] = useState(null);
  const [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [models, setModels] = useState([]);
  const [aiModel, setAiModel] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [attachedFile, setAttachedFile] = useState(null); // { name, content }
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const activeMessages = useMemo(() => activeThread?.messages || [], [activeThread]);

  const loadThreads = useCallback(async (q = "") => {
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const resp = await api.get(`/chat/threads${params}`);
      const next = resp.data?.threads || [];
      setThreads(next);
      if (!activeThreadId && next.length > 0) setActiveThreadId(next[0].thread_id);
    } catch { toast.error("Unable to load chat threads"); }
  }, [api, activeThreadId]);

  const loadThread = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const resp = await api.get(`/chat/threads/${threadId}`);
      setActiveThread(resp.data || null);
    } catch { toast.error("Unable to load thread"); }
  }, [api]);

  useEffect(() => {
    const bootstrap = async () => {
      await loadThreads();
      try {
        const resp = await api.get("/models/available");
        const modelList = resp.data?.models || [];
        setModels(modelList);
        if (modelList.length > 0) setAiModel(modelList[0]);
      } catch (e) { console.error("Failed to load models:", e); }
    };
    bootstrap();
  }, [api, loadThreads]);

  useEffect(() => { if (activeThreadId) loadThread(activeThreadId); }, [activeThreadId, loadThread]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [activeMessages]);

  const togglePin = async (threadId) => {
    try {
      const resp = await api.patch(`/chat/threads/${threadId}/pin`);
      setThreads((prev) =>
        prev.map((t) => t.thread_id === threadId ? { ...t, pinned: resp.data.pinned } : t)
          .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)));
    } catch { toast.error("Failed to pin thread"); }
  };

  const createThread = async () => {
    if (!aiModel) { toast.error("Select an AI model first"); return; }
    setCreating(true);
    try {
      const resp = await api.post("/chat/threads", { topic: "", ai_model: aiModel });
      setThreads((prev) => [resp.data, ...prev]);
      setActiveThreadId(resp.data.thread_id);
      setActiveThread(resp.data);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch { toast.error("Unable to create thread"); }
    finally { setCreating(false); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const ext = file.name.split(".").pop()?.toLowerCase();
    const clientTextTypes = ["txt", "md", "markdown", "json", "csv"];
    const serverTypes = ["pdf", "docx", "xlsx", "pptx", "png", "jpg", "jpeg", "webp", "gif"];

    if (clientTextTypes.includes(ext)) {
      const text = await file.text();
      setAttachedFile({ name: file.name, content: text.slice(0, 50000) });
      toast.success(`Attached: ${file.name}`);
    } else if (serverTypes.includes(ext)) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await api.post("/chat/extract-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachedFile({ name: file.name, content: resp.data.text, isImage: resp.data.is_image, imageBase64: resp.data.image_base64, imageMime: resp.data.image_mime });
        const label = resp.data.is_image ? `Image attached: ${file.name}` : `Extracted ${resp.data.char_count.toLocaleString()} chars from ${file.name}`;
        toast.success(label);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to process file");
      } finally { setUploading(false); }
    } else {
      toast.error("Unsupported file type. Use PDF, Word, Excel, PowerPoint, images, or text files.");
    }
  };

  const sendMessage = async () => {
    if (!message.trim() && !attachedFile) return;
    if (!aiModel) { toast.error("Select an AI model"); return; }

    // Auto-create thread if none exists
    let threadId = activeThreadId;
    if (!threadId) {
      setCreating(true);
      try {
        const resp = await api.post("/chat/threads", { topic: "", ai_model: aiModel });
        setThreads((prev) => [resp.data, ...prev]);
        setActiveThreadId(resp.data.thread_id);
        setActiveThread(resp.data);
        threadId = resp.data.thread_id;
      } catch { toast.error("Unable to create thread"); setCreating(false); return; }
      finally { setCreating(false); }
    }

    const payload = {
      content: message.trim() || (attachedFile ? `Analyze this file: ${attachedFile.name}` : ""),
      ai_model: aiModel,
    };
    if (attachedFile) {
      payload.file_content = attachedFile.content;
      payload.file_name = attachedFile.name;
    }

    setSending(true);
    setMessage("");
    setAttachedFile(null);

    // Optimistic: show user message immediately
    const tempUserMsg = { message_id: `temp_${Date.now()}`, role: "user", content: payload.content + (attachedFile ? `\n\n[Attached: ${attachedFile.name}]` : ""), created_at: new Date().toISOString() };
    setActiveThread((prev) => ({ ...(prev || {}), messages: [...(prev?.messages || []), tempUserMsg] }));

    try {
      const resp = await api.post(`/chat/threads/${threadId}/messages`, payload);
      const { user_message, assistant_message } = resp.data;
      setActiveThread((prev) => ({
        ...(prev || {}),
        messages: [...(prev?.messages || []).filter(m => m.message_id !== tempUserMsg.message_id), user_message, assistant_message],
      }));
      await loadThreads(searchQuery);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error_message || "Message failed");
      // Remove optimistic message on failure
      setActiveThread((prev) => ({ ...(prev || {}), messages: (prev?.messages || []).filter(m => m.message_id !== tempUserMsg.message_id) }));
      setMessage(payload.content);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <NoBusinessGuard>
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4" style={{ height: "calc(100vh - 100px)" }}>
            {/* Sidebar */}
            <aside className="flex flex-col bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden" data-testid="chat-thread-list">
              <div className="p-3 space-y-2 border-b border-[var(--border)]">
                <Button onClick={createThread} disabled={creating} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="chat-new-thread-btn">
                  {creating ? <SpinnerGap className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />} New Chat
                </Button>
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); loadThreads(e.target.value); }}
                    placeholder="Search..." className="w-full pl-8 pr-3 py-1.5 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                    data-testid="chat-search-input" />
                </div>
              </div>
              <div className="flex-1 overflow-auto p-2 space-y-0.5">
                {threads.map((thread) => (
                  <div key={thread.thread_id} className={`relative group rounded-lg transition-colors ${activeThreadId === thread.thread_id ? "bg-[#FF4400]/10" : "hover:bg-[var(--bg-hover)]"}`}>
                    <button onClick={() => setActiveThreadId(thread.thread_id)} className="w-full text-left p-2.5 pr-8" data-testid={`chat-thread-${thread.thread_id}`}>
                      <div className="flex items-center gap-1.5">
                        {thread.pinned && <PushPin size={10} weight="fill" className="text-[#FF4400] shrink-0" />}
                        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{thread.topic || "New chat"}</p>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">{thread.last_message_preview || "No messages yet"}</p>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); togglePin(thread.thread_id); }}
                      className={`absolute top-2.5 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${thread.pinned ? "opacity-100 text-[#FF4400]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
                      data-testid={`chat-pin-${thread.thread_id}`}>
                      <PushPin size={12} weight={thread.pinned ? "fill" : "regular"} />
                    </button>
                  </div>
                ))}
                {threads.length === 0 && <p className="text-xs text-[var(--text-muted)] p-3 text-center">No conversations yet</p>}
              </div>
            </aside>

            {/* Chat area */}
            <section className="flex flex-col bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden" data-testid="chat-message-area">
              {/* Model selector bar */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--bg-base)]/50">
                <span className="text-sm font-medium text-[var(--text-primary)]">{activeThread?.topic || "Chat"}</span>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger className="w-44 h-8 bg-[var(--bg-card)] border-[var(--border-strong)] text-xs" data-testid="chat-model-select">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>{models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
                {activeMessages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center" data-testid="chat-empty-state">
                    <ChatCircleDots size={40} className="text-[var(--text-muted)] mb-3" />
                    <p className="text-sm text-[var(--text-muted)]">Start a conversation. Ask anything — strategy, content, analysis.</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">Upload a PDF, text, or JSON file for deeper analysis.</p>
                  </div>
                )}
                {activeMessages.map((m) => (
                  <div key={m.message_id} className={`group flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`chat-msg-${m.message_id}`}>
                    {m.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-[#FF4400]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <ChatCircleDots size={14} className="text-[#FF4400]" />
                      </div>
                    )}
                    <div className={`relative max-w-[80%] rounded-xl px-4 py-3 ${m.role === "user"
                      ? "bg-[#FF4400] text-white rounded-br-sm"
                      : "bg-[var(--bg-raised)] border border-[var(--border)] rounded-bl-sm"}`}>
                      {m.role === "assistant" ? (
                        <div className="prose prose-sm prose-invert max-w-none text-[var(--text-primary)]
                          prose-headings:text-[var(--text-primary)] prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
                          prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                          prose-strong:text-[var(--text-primary)] prose-code:text-[#FF4400] prose-code:bg-[var(--bg-base)] prose-code:px-1 prose-code:rounded
                          prose-blockquote:border-l-[#FF4400] prose-blockquote:text-[var(--text-secondary)]">
                          <ReactMarkdown>{m.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                      )}
                      {/* Copy button */}
                      <div className="absolute -bottom-6 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <CopyButton text={m.content} />
                      </div>
                    </div>
                    {m.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-[var(--bg-raised)] border border-[var(--border)] flex items-center justify-center shrink-0 mt-0.5">
                        <User size={14} className="text-[var(--text-muted)]" />
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#FF4400]/10 flex items-center justify-center shrink-0">
                      <SpinnerGap size={14} className="text-[#FF4400] animate-spin" />
                    </div>
                    <div className="bg-[var(--bg-raised)] border border-[var(--border)] rounded-xl rounded-bl-sm px-4 py-3">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-[var(--text-muted)] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-[var(--text-muted)] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-[var(--text-muted)] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <div className="border-t border-[var(--border)] p-3">
                {attachedFile && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg text-xs" data-testid="chat-attached-file">
                    <Paperclip size={12} className="text-[#FF4400] shrink-0" />
                    <span className="text-[var(--text-primary)] truncate">{attachedFile.name}</span>
                    <span className="text-[var(--text-muted)]">({(attachedFile.content.length / 1000).toFixed(0)}k chars)</span>
                    <button onClick={() => setAttachedFile(null)} className="ml-auto p-0.5 hover:bg-[var(--bg-hover)] rounded" data-testid="chat-remove-file">
                      <X size={12} className="text-[var(--text-muted)]" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf,.txt,.md,.json,.csv,.markdown,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp,.gif" className="hidden" data-testid="chat-file-input" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors shrink-0" title="Attach file" data-testid="chat-attach-btn">
                    {uploading ? <SpinnerGap size={18} className="animate-spin" /> : <Paperclip size={18} />}
                  </button>
                  <div className="flex-1 relative">
                    <textarea ref={inputRef} value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={handleKeyDown}
                      placeholder="Type a message..." rows={1}
                      className="w-full resize-none bg-[var(--bg-base)] border border-[var(--border)] rounded-xl px-4 py-2.5 pr-20 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400] min-h-[42px] max-h-[160px]"
                      style={{ height: "auto", overflow: "hidden" }}
                      onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px"; }}
                      data-testid="chat-message-input" />
                    <div className="absolute right-2 bottom-1.5 flex items-center gap-1">
                      <VoiceDictationButton onTranscript={(text) => setMessage((prev) => prev ? `${prev} ${text}` : text)} className="w-7 h-7" />
                      <button onClick={sendMessage} disabled={sending || (!message.trim() && !attachedFile)}
                        className="w-8 h-8 rounded-lg bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors" data-testid="chat-send-btn">
                        <PaperPlaneRight size={16} weight="fill" className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </NoBusinessGuard>
  );
}
